import { usePathname } from 'next/navigation';
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';

export interface DatePickerContextProps {
  successModalIsOpen: boolean;
  setSuccessModalIsOpen: Dispatch<SetStateAction<boolean>>;
  optionedDates:
    | {
        timestampStart: string;
        timestampEnd: string;
      }
    | undefined;
  setOptionedDates: Dispatch<
    SetStateAction<
      | {
          timestampStart: string;
          timestampEnd: string;
        }
      | undefined
    >
  >;
  checkInDays: string[] | undefined;
  setCheckInDays: Dispatch<SetStateAction<string[] | undefined>>;
  calendarErrorTypes: string[];
  setCalendarErrorTypes: Dispatch<SetStateAction<string[]>>;
  enquiryModalIsOpen: boolean;
  setEnquiryModalIsOpen: Dispatch<SetStateAction<boolean>>;
  handleCalendarErrorType: (errorType: string) => void;
  clearCalendarErrorType: (errorType: string) => void;
  clearCalendarErrorTypes(errorTypes: string[]): void;
  calendarHasErrorType: (errorType: string) => boolean;
  clearCalendarFocus(dates: { start: Date | null; end: Date | null }): void;
  resetCalendarData(): void;
}

interface DatePickerProviderProps {
  children: ReactNode;
}

const DatePickerContext = createContext<DatePickerContextProps>(
  {} as DatePickerContextProps,
);

export const DatePickerContextProvider: FC<DatePickerProviderProps> = ({
  children,
}) => {
  const [successModalIsOpen, setSuccessModalIsOpen] = useState(false);
  const [optionedDates, setOptionedDates] = useState<{
    timestampStart: string;
    timestampEnd: string;
  }>();
  const [enquiryModalIsOpen, setEnquiryModalIsOpen] = useState(false);
  const [calendarErrorTypes, setCalendarErrorTypes] = useState<string[]>([]);
  const [checkInDays, setCheckInDays] = useState<string[]>();
  const pathname = usePathname();

  useEffect(() => {
    // reset calendar data when the url changes, preventing showing errors from the previous calendars
    resetCalendarData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  function handleCalendarErrorType(errorType: string) {
    setCalendarErrorTypes([...calendarErrorTypes, errorType]);
  }

  function clearCalendarErrorType(errorType: string) {
    setCalendarErrorTypes((prev) =>
      prev.filter((error) => error !== errorType),
    );
  }

  function calendarHasErrorType(errorType: string) {
    return calendarErrorTypes.includes(errorType);
  }

  function clearCalendarErrorTypes(errorTypes: string[]) {
    setCalendarErrorTypes((prev) =>
      prev.filter((error) => !errorTypes.includes(error)),
    );
  }

  function clearCalendarFocus(dates: { start: Date | null; end: Date | null }) {
    const buggedFocusedButton = document.querySelector(
      'button[data-range-start="true"][data-range-end="true"][data-highlighted-inactive="true"]',
    ) as HTMLButtonElement | null;

    const rangeStartButton = document.querySelector(
      `button[data-range-start="true"][data-calendar-value="${dates.start?.getTime()}"]`,
    ) as HTMLButtonElement | null;

    const userClearedDateBeforeConfirmation = rangeStartButton != null;
    const rangeNotSelected = dates.end == null;

    if (buggedFocusedButton) {
      buggedFocusedButton.click();
    } else {
      if (userClearedDateBeforeConfirmation && rangeNotSelected) {
        rangeStartButton.click();
      }
    }
  }

  function resetCalendarData() {
    setSuccessModalIsOpen(false);
    setOptionedDates(undefined);
    setEnquiryModalIsOpen(false);
    setCalendarErrorTypes([]);
    setCheckInDays(undefined);
  }

  return (
    <DatePickerContext.Provider
      value={{
        successModalIsOpen,
        setSuccessModalIsOpen,
        optionedDates,
        setOptionedDates,
        enquiryModalIsOpen,
        setEnquiryModalIsOpen,
        calendarErrorTypes,
        setCalendarErrorTypes,
        clearCalendarErrorType,
        calendarHasErrorType,
        handleCalendarErrorType,
        checkInDays,
        setCheckInDays,
        clearCalendarErrorTypes,
        clearCalendarFocus,
        resetCalendarData,
      }}>
      {children}
    </DatePickerContext.Provider>
  );
};

export default DatePickerContext;
